import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, 
  Send, 
  Pin, 
  AlertTriangle, 
  ClipboardList, 
  Users, 
  Search, 
  Trash2, 
  Sparkles, 
  CheckCircle2, 
  ShieldCheck, 
  Clock, 
  Hash,
  Filter,
  Check,
  UserCheck
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { 
  AdminChatMessage, 
  ChatChannel, 
  MessagePriority, 
  sendAdminMessage, 
  subscribeToAdminMessages, 
  togglePinMessage, 
  deleteAdminMessage 
} from '../services/adminChatService';
import { 
  AdminPresenceRecord, 
  subscribeToAdminPresence 
} from '../services/adminPresenceService';

export const AdminChatView: React.FC = () => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<AdminChatMessage[]>([]);
  const [onlineAdmins, setOnlineAdmins] = useState<AdminPresenceRecord[]>([]);
  const [activeChannel, setActiveChannel] = useState<ChatChannel | 'all'>('all');
  const [inputText, setInputText] = useState('');
  const [priority, setPriority] = useState<MessagePriority>('normal');
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Subscribe to messages
  useEffect(() => {
    const unsub = subscribeToAdminMessages(activeChannel, (newMsgs) => {
      setMessages(newMsgs);
    });
    return () => unsub();
  }, [activeChannel]);

  // Subscribe to online team presence
  useEffect(() => {
    const unsub = subscribeToAdminPresence((presences) => {
      setOnlineAdmins(presences.filter((p) => p.status === 'online' || p.status === 'away'));
    });
    return () => unsub();
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || !currentUser || sending) return;

    setSending(true);
    try {
      await sendAdminMessage({
        senderUid: currentUser.uid,
        senderEmail: currentUser.email,
        senderName: currentUser.displayName || currentUser.email.split('@')[0],
        senderRole: currentUser.role || 'super_admin',
        senderAvatar: currentUser.avatarUrl,
        text: inputText.trim(),
        channel: activeChannel === 'all' ? 'broadcast' : activeChannel,
        priority,
      });

      setInputText('');
      setPriority('normal');
    } catch (err) {
      console.error('Failed to send admin message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleTogglePin = async (msgId: string, currentPin: boolean) => {
    try {
      await togglePinMessage(msgId, !currentPin);
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    }
  };

  const handleDelete = async (msgId: string) => {
    if (window.confirm('Delete this message for all admins?')) {
      try {
        await deleteAdminMessage(msgId);
      } catch (err) {
        console.error('Failed to delete message:', err);
      }
    }
  };

  const pinnedMessages = messages.filter((m) => m.pinned);
  const filteredMessages = messages.filter((m) => {
    if (!searchQuery.trim()) return true;
    return (
      m.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.senderName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-black tracking-tight text-white dark:text-white light:text-[#0D1C17]">
              Admin Team Chat & Handover Desk
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              {onlineAdmins.length} Staff On Duty
            </span>
          </div>
          <p className="text-sm text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-1">
            Internal communication stream, shift handovers, and operational alert channel for ReUseIt staff.
          </p>
        </div>
      </div>

      {/* Main Container */}
      <div className="rounded-3xl border border-[#22493c] bg-[#152a23] dark:bg-[#152a23] light:bg-white light:border-[#D5E4DC] shadow-card overflow-hidden grid grid-cols-1 lg:grid-cols-4 min-h-[640px]">
        {/* Left Sidebar: Channels & Online Staff */}
        <div className="p-5 border-b lg:border-b-0 lg:border-r border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] bg-[#10231d]/60 space-y-6">
          {/* Channels */}
          <div>
            <span className="text-[10px] font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider block mb-2">
              Communication Channels
            </span>
            <div className="space-y-1">
              {[
                { id: 'all', label: 'All Messages', icon: Hash },
                { id: 'broadcast', label: '📢 Staff Broadcast', icon: MessageSquare },
                { id: 'handover', label: '📋 Shift Handover', icon: ClipboardList },
                { id: 'escrow_alert', label: '🚨 Escrow Alerts', icon: AlertTriangle },
                { id: 'disputes', label: '⚖️ Dispute Tribunal', icon: ShieldCheck },
              ].map((ch) => {
                const Icon = ch.icon;
                const isActive = activeChannel === ch.id;
                return (
                  <button
                    key={ch.id}
                    onClick={() => setActiveChannel(ch.id as any)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                      isActive
                        ? 'bg-brand-500 text-forest-void font-bold shadow-glow-sm'
                        : 'text-[#90cbb7] hover:text-white hover:bg-[#152a23]'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{ch.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active Staff On Duty */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider">
                Staff On Duty ({onlineAdmins.length})
              </span>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {onlineAdmins.length === 0 ? (
                <p className="text-xs text-[#90cbb7]/60 italic">No other staff members active</p>
              ) : (
                onlineAdmins.map((admin) => (
                  <div 
                    key={admin.uid}
                    className="p-2.5 rounded-2xl bg-[#10231d] border border-[#22493c] flex items-center gap-2.5"
                  >
                    <div className="relative">
                      {admin.avatarUrl ? (
                        <img src={admin.avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-brand-500/20 text-brand-400 font-bold flex items-center justify-center text-xs">
                          {admin.displayName?.charAt(0) || 'A'}
                        </div>
                      )}
                      <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#10231d] ${
                        admin.status === 'online' ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'
                      }`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-xs truncate text-white dark:text-white light:text-[#0D1C17]">
                        {admin.displayName}
                      </p>
                      <p className="text-[10px] text-[#90cbb7] truncate capitalize">
                        {admin.role.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Pane: Message Stream & Compose */}
        <div className="lg:col-span-3 flex flex-col h-full">
          {/* Top Search & Channel Indicator */}
          <div className="p-4 border-b border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] flex items-center justify-between gap-4 bg-[#10231d]/30">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-white dark:text-white light:text-[#0D1C17] capitalize">
                #{activeChannel.replace('_', ' ')}
              </span>
              <span className="text-xs text-[#90cbb7]">
                ({filteredMessages.length} messages)
              </span>
            </div>

            <div className="relative w-48 sm:w-64">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#90cbb7]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search notes or staff..."
                className="w-full pl-8 pr-3 py-1.5 rounded-xl text-xs bg-[#10231d] border border-[#22493c] text-white placeholder-[#90cbb7]/60 outline-none focus:border-brand-500"
              />
            </div>
          </div>

          {/* Pinned Handover Notes Banner */}
          {pinnedMessages.length > 0 && (
            <div className="p-3 bg-amber-500/10 border-b border-amber-500/20 text-xs">
              <div className="flex items-center gap-2 font-bold text-amber-300 text-[11px] mb-1.5">
                <Pin className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                <span>Pinned Shift Handover Notes</span>
              </div>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {pinnedMessages.map((p) => (
                  <div key={p.id} className="text-amber-200/90 text-[11px] flex items-start gap-2">
                    <span className="font-semibold text-white">• {p.senderName}:</span>
                    <span className="truncate flex-1">{p.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Message List */}
          <div className="flex-1 p-5 space-y-3.5 overflow-y-auto max-h-[460px]">
            {filteredMessages.length === 0 ? (
              <div className="h-64 flex flex-col items-center justify-center text-center text-[#90cbb7]">
                <MessageSquare className="w-12 h-12 stroke-1 mb-3 text-[#90cbb7]/40" />
                <p className="font-bold text-base text-white">No messages in this channel yet</p>
                <p className="text-xs text-[#90cbb7]/70 mt-1 max-w-sm">
                  Start the shift conversation, leave a handover checklist, or alert the team regarding escrow transactions.
                </p>
              </div>
            ) : (
              filteredMessages.map((msg) => {
                const isMe = msg.senderUid === currentUser?.uid;
                const isHandover = msg.priority === 'handover';
                const isUrgent = msg.priority === 'urgent';

                return (
                  <div 
                    key={msg.id} 
                    className={`rounded-2xl p-4 text-xs transition-all relative group ${
                      msg.pinned 
                        ? 'bg-amber-500/10 border border-amber-500/30 shadow-sm' 
                        : isUrgent
                          ? 'bg-rose-500/10 border border-rose-500/30'
                          : isHandover
                            ? 'bg-emerald-500/10 border border-emerald-500/30'
                            : isMe
                              ? 'bg-brand-500/10 border border-brand-500/25 ml-12'
                              : 'bg-[#10231d] border border-[#22493c] mr-12'
                    }`}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        {msg.senderAvatar ? (
                          <img src={msg.senderAvatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-brand-500/20 text-brand-400 font-bold flex items-center justify-center text-[10px]">
                            {msg.senderName.charAt(0)}
                          </div>
                        )}
                        <span className="font-bold text-white text-xs">
                          {isMe ? 'You' : msg.senderName}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded font-semibold bg-[#22493c] text-[#90cbb7] capitalize">
                          {msg.senderRole.replace('_', ' ')}
                        </span>
                        {isHandover && (
                          <span className="text-[10px] px-2 py-0.5 rounded font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                            <ClipboardList className="w-3 h-3" />
                            Handover Note
                          </span>
                        )}
                        {isUrgent && (
                          <span className="text-[10px] px-2 py-0.5 rounded font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30 flex items-center gap-1 animate-pulse">
                            <AlertTriangle className="w-3 h-3" />
                            Urgent Alert
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-[#90cbb7]/60">
                          {msg.createdAt?.toDate 
                            ? new Date(msg.createdAt.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : 'Just now'
                          }
                        </span>

                        {/* Actions */}
                        <div className="hidden group-hover:flex items-center gap-1">
                          <button
                            onClick={() => handleTogglePin(msg.id, !!msg.pinned)}
                            title={msg.pinned ? 'Unpin note' : 'Pin handover note'}
                            className="p-1 rounded text-[#90cbb7] hover:text-white hover:bg-[#152a23]"
                          >
                            <Pin className="w-3.5 h-3.5" />
                          </button>
                          {(isMe || currentUser?.role === 'super_admin') && (
                            <button
                              onClick={() => handleDelete(msg.id)}
                              title="Delete message"
                              className="p-1 rounded text-rose-400 hover:text-rose-300 hover:bg-[#152a23]"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Text Body */}
                    <p className="text-white/95 leading-relaxed text-xs whitespace-pre-wrap">
                      {msg.text}
                    </p>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Composer */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] bg-[#10231d]/60 space-y-3">
            {/* Tag Selection Row */}
            <div className="flex items-center justify-between text-xs gap-3">
              <div className="flex items-center gap-2">
                <span className="text-[#90cbb7]">Tag:</span>
                <button
                  type="button"
                  onClick={() => setPriority('normal')}
                  className={`px-3 py-1 rounded-xl text-xs font-bold transition-all ${
                    priority === 'normal' 
                      ? 'bg-brand-500 text-forest-void shadow-glow-sm' 
                      : 'bg-[#10231d] text-[#90cbb7] border border-[#22493c]'
                  }`}
                >
                  Normal
                </button>
                <button
                  type="button"
                  onClick={() => setPriority('handover')}
                  className={`px-3 py-1 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                    priority === 'handover' 
                      ? 'bg-amber-500 text-forest-void shadow-sm' 
                      : 'bg-[#10231d] text-amber-300 border border-amber-500/30'
                  }`}
                >
                  <ClipboardList className="w-3.5 h-3.5" />
                  Shift Handover
                </button>
                <button
                  type="button"
                  onClick={() => setPriority('urgent')}
                  className={`px-3 py-1 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                    priority === 'urgent' 
                      ? 'bg-rose-500 text-white shadow-sm' 
                      : 'bg-[#10231d] text-rose-300 border border-rose-500/30'
                  }`}
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Urgent Alert
                </button>
              </div>

              {/* Quick handover macros */}
              <div className="hidden sm:flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setInputText('📋 Shift Handover: All pending 48-hr inspections checked. No escalated disputes.')}
                  className="px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-[#152a23] hover:bg-[#1c382f] text-[#90cbb7] border border-[#22493c]"
                >
                  + Checklist Macro
                </button>
                <button
                  type="button"
                  onClick={() => setInputText('🚨 Escrow Alert: Please review payout release for high-value order.')}
                  className="px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-[#152a23] hover:bg-[#1c382f] text-[#90cbb7] border border-[#22493c]"
                >
                  + Escrow Macro
                </button>
              </div>
            </div>

            {/* Input & Send Button */}
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type your message, leave a shift handover note, or tag an operational alert..."
                className="flex-1 px-4 py-3 rounded-2xl text-xs bg-[#10231d] border border-[#22493c] text-white placeholder-[#90cbb7]/60 outline-none focus:border-brand-500"
              />
              <button
                type="submit"
                disabled={!inputText.trim() || sending}
                className="px-5 py-3 rounded-2xl bg-brand-500 hover:bg-brand-400 disabled:opacity-40 text-forest-void font-bold transition-all shadow-glow-sm flex items-center justify-center gap-2 shrink-0 text-xs"
              >
                <Send className="w-4 h-4" />
                <span>Send</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
